import { ChapterHeading } from "@/components/ChapterHeading";
import { Reveal } from "@/components/Reveal";
import "@/styles/theme-v2.css";

// ============================================================================
// CHAPTER 01 — THE MEHFIL. Visual-richness pass only: same copy, same
// heading, same anchor. The left column used to end at the chapter heading
// and leave a large empty column beneath it on desktop — this adds a framed
// atmosphere panel there, cropped from the site's own official poster
// artwork (frontend/public/poster.jpg — the same approved asset already
// shown in the Hero), not a new or invented image. The crop shows only the
// poster's ornamental curtain-and-lantern corner, with no text in frame.
// ============================================================================

export function Experience() {
  return (
    <section id="experience" data-testid="experience-section" className="relative overflow-hidden px-5 py-20 sm:px-8 sm:py-28 lg:py-36">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -left-24 top-1/3 h-[420px] w-[420px] rounded-full bg-[#D4AF37]/[0.07] blur-[120px]" />
      </div>
      <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <ChapterHeading number="01" title="The Mehfil" urdu="محفل" testId="experience-heading" />

          {/* Compact mobile/tablet version of the same atmosphere image used
              in the desktop panel below (same file, same crop of the site's
              own approved poster artwork) — a short band rather than a tall
              portrait, so Chapter 01 isn't text-only on phones without adding
              much vertical height. Hidden at lg+ where the full panel takes over. */}
          <Reveal delay={0.12} className="mt-8 block lg:hidden">
            <div className="cah-light-sweep relative h-40 w-full overflow-hidden rounded-3xl border border-[#D4AF37]/25 bg-[#0E0C0A] sm:h-48">
              <img
                src="/textures/mehfil-lantern.jpg"
                alt=""
                aria-hidden="true"
                data-testid="experience-atmosphere-photo-mobile"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div
                className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(8,7,5,0.1)_0%,transparent_40%,rgba(8,7,5,0.5)_100%)]"
                aria-hidden="true"
              />
              <div className="pointer-events-none absolute inset-[6px] border border-[#F3E5AB]/10" aria-hidden="true" />
            </div>
          </Reveal>

          <Reveal delay={0.16} className="mt-10 hidden lg:block">
            <div className="relative mx-auto max-w-[280px]">
              <div className="arch-frame relative aspect-[3/4.2] w-full overflow-hidden border border-[#D4AF37]/30 bg-[#0E0C0A]">
                <img
                  src="/textures/mehfil-lantern.jpg"
                  alt=""
                  aria-hidden="true"
                  data-testid="experience-atmosphere-photo"
                  className="cah-kenburns absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                />
                <div
                  className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(8,7,5,0.55),transparent_60%)]"
                  aria-hidden="true"
                />
                <div className="pointer-events-none absolute inset-[8px] border border-[#F3E5AB]/15" aria-hidden="true" />
              </div>
              <div
                className="arch-frame absolute -right-4 top-4 -z-10 h-full w-full border border-[#D4AF37]/15"
                aria-hidden="true"
              />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <p className="font-accent text-2xl italic leading-snug text-[#F3E5AB] sm:text-3xl lg:text-4xl">
              Some evenings are concerts. This one is a mehfil.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-8 text-base leading-[1.8] text-[#C8C2B7] sm:text-lg">
              Chitti Ayee Hai — “the letter has arrived” — is an evening of live ghazal,
              qawwali and sufi music in Bengaluru. Poetry sung the way it was written to be heard:
              unhurried, heartfelt, and close enough to touch.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <p className="mt-6 text-base leading-[1.8] text-[#C8C2B7] sm:text-lg">
              For one night, the stage at Dr. B.R. Ambedkar Bhavan becomes a gathering of words,
              rhythm and devotion — the kind of evening you bring your parents to, and talk about
              long after the last note settles.
            </p>
          </Reveal>
          <Reveal delay={0.34}>
            <p className="font-display mt-10 text-xl text-[#D4AF37] sm:text-2xl">
              Come as you are. Leave a little lighter.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
